import { apiFetch } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';

export interface CoinPackage {
  code: string;
  coins: number;
  amountInrPaise: number;
  amountUsdCents: number;
  discountPct: number;
  popular: boolean;
}

export type PurchaseCurrency = 'INR' | 'USD';

export interface CreatePurchaseOrderResponse {
  orderId: string;
  razorpayOrderId: string;
  amount: number;
  currency: string;
  keyId: string;
  coins: number;
}

export interface VerifyPurchasePaymentPayload {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface PurchaseOrderStatus {
  orderId: string;
  status: 'CREATED' | 'PAID' | 'FAILED';
  coins: number;
  newCoinBalance: number | null;
}

export interface PurchaseOrderHistory {
  orderId: string;
  razorpayOrderId: string;
  razorpayPaymentId: string | null;
  coins: number;
  amount: number;       // smallest currency unit (paise / cents)
  currency: string;
  status: 'PAID' | 'DISPUTED' | 'REFUNDED';
  paidAt: string | null;
  createdAt: string;
}

export async function fetchPurchaseOrders(accessToken: string): Promise<PurchaseOrderHistory[]> {
  const response = await apiFetch('/api/v1/coins/purchase-orders', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch purchase history: ${response.status}`);
  return response.json();
}

// Never wrap these in apiFetchWithRetry — all three are non-idempotent
// mutations from Razorpay's perspective (order creation and payment
// verification), same rule the rest of this file's siblings follow.

export async function fetchCoinPackages(): Promise<CoinPackage[]> {
  const response = await apiFetch('/api/v1/coins/packages');
  if (!response.ok) throw new Error(`Failed to fetch coin packages: ${response.status}`);
  return response.json();
}

export async function createPurchaseOrder(
  accessToken: string,
  packageCode: string,
  currency: PurchaseCurrency,
  buyerRole: 'STUDENT' | 'TEACHER'
): Promise<CreatePurchaseOrderResponse> {
  const response = await apiFetch('/api/v1/coins/purchase-orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ packageCode, currency, buyerRole }),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to create purchase order: ${response.status}`);
  return response.json();
}

export async function verifyPurchasePayment(
  accessToken: string,
  orderId: string,
  payload: VerifyPurchasePaymentPayload
): Promise<PurchaseOrderStatus> {
  const response = await apiFetch(`/api/v1/coins/purchase-orders/${orderId}/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to verify payment: ${response.status}`);
  return response.json();
}
